import React, { useState, useEffect } from "react";
import {
  Utensils,
  CreditCard,
  Users,
  TrendingUp,
  ShoppingCart,
  Plus,
  Minus,
  CreditCard as Card,
  Smartphone,
  Shield,
  Bell,
  Settings,
  Download,
  Eye,
  Lock,
  AlertTriangle,
  CheckCircle,
  X,
  Search,
  Filter,
  Package,
  BarChart3,
  AlertCircle,
  Edit,
  Trash2,
} from "lucide-react";
import "./CafeteriaSystem.css";

const CafeteriaSystem = () => {
  const [activeTab, setActiveTab] = useState("pos");
  const [cart, setCart] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showParentControls, setShowParentControls] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [showAddFundsModal, setShowAddFundsModal] = useState(false);
  const [selectedStudentForModal, setSelectedStudentForModal] = useState(null);
  const [newStudent, setNewStudent] = useState({
    name: "",
    grade: "",
    parent: "",
    initialBalance: 0,
    dailyLimit: 20,
    restrictions: [],
  });
  const [fundsAmount, setFundsAmount] = useState(0);
  const [reportPeriod, setReportPeriod] = useState("Today");
  const [notifications, setNotifications] = useState([]);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [selectedItemForEdit, setSelectedItemForEdit] = useState(null);
  const [newItem, setNewItem] = useState({
    name: "",
    category: "",
    price: 0,
    stock: 0,
    minStock: 5,
    supplier: "",
    allergens: [],
    image: "",
  });

  const [inventory, setInventory] = useState([
    {
      id: 1,
      name: "Chicken Burger",
      price: 8.5,
      category: "Main Course",
      image: "🍔",
      available: true,
      allergens: ["gluten", "dairy"],
      stock: 25,
      minStock: 5,
      supplier: "Fresh Foods Co.",
      lastRestocked: "2024-01-10",
      cost: 4.5,
    },
    {
      id: 2,
      name: "Caesar Salad",
      price: 6.0,
      category: "Salad",
      image: "🥗",
      available: true,
      allergens: ["dairy", "nuts"],
      stock: 15,
      minStock: 3,
      supplier: "Green Valley Farms",
      lastRestocked: "2024-01-12",
      cost: 3.2,
    },
    {
      id: 3,
      name: "Pizza Slice",
      price: 4.5,
      category: "Main Course",
      image: "🍕",
      available: true,
      allergens: ["gluten", "dairy"],
      stock: 8,
      minStock: 10,
      supplier: "Pizza Palace",
      lastRestocked: "2024-01-08",
      cost: 2.8,
    },
    {
      id: 4,
      name: "Fresh Juice",
      price: 3.0,
      category: "Beverage",
      image: "🥤",
      available: true,
      allergens: [],
      stock: 30,
      minStock: 8,
      supplier: "Juice Bar Inc.",
      lastRestocked: "2024-01-14",
      cost: 1.5,
    },
    {
      id: 5,
      name: "Chocolate Cake",
      price: 2.5,
      category: "Dessert",
      image: "🍰",
      available: false,
      allergens: ["gluten", "dairy", "eggs"],
      stock: 0,
      minStock: 5,
      supplier: "Sweet Treats Bakery",
      lastRestocked: "2024-01-05",
      cost: 1.8,
    },
    {
      id: 6,
      name: "Grilled Chicken Wrap",
      price: 7.5,
      category: "Main Course",
      image: "🌯",
      available: true,
      allergens: ["gluten"],
      stock: 2,
      minStock: 5,
      supplier: "Wrap Masters",
      lastRestocked: "2024-01-09",
      cost: 4.2,
    },
    {
      id: 7,
      name: "Fruit Bowl",
      price: 4.0,
      category: "Salad",
      image: "🍓",
      available: true,
      allergens: [],
      stock: 12,
      minStock: 4,
      supplier: "Fresh Fruits Ltd.",
      lastRestocked: "2024-01-13",
      cost: 2.5,
    },
    {
      id: 8,
      name: "Energy Drink",
      price: 2.0,
      category: "Beverage",
      image: "⚡",
      available: true,
      allergens: [],
      stock: 45,
      minStock: 10,
      supplier: "Energy Plus",
      lastRestocked: "2024-01-11",
      cost: 1.2,
    },
  ]);

  const mockMenu = inventory;

  const [students, setStudents] = useState([
    {
      id: "STU001",
      name: "Emma Wilson",
      grade: "Grade 10",
      balance: 45.5,
      dailyLimit: 20.0,
      spentToday: 12.5,
      restrictions: ["nuts", "dairy"],
      parent: "David Wilson",
    },
    {
      id: "STU002",
      name: "James Smith",
      grade: "Grade 9",
      balance: 23.75,
      dailyLimit: 15.0,
      spentToday: 8.25,
      restrictions: ["gluten"],
      parent: "Sarah Smith",
    },
    {
      id: "STU003",
      name: "Sarah Davis",
      grade: "Grade 11",
      balance: 67.0,
      dailyLimit: 25.0,
      spentToday: 0.0,
      restrictions: [],
      parent: "Michael Davis",
    },
  ]);

  const mockTransactions = [
    {
      id: 1,
      student: "Emma Wilson",
      studentId: "STU001",
      items: ["Chicken Burger", "Fresh Juice"],
      total: 11.5,
      timestamp: "2024-01-15 12:30:00",
      status: "completed",
    },
    {
      id: 2,
      student: "James Smith",
      studentId: "STU002",
      items: ["Caesar Salad"],
      total: 6.0,
      timestamp: "2024-01-15 12:15:00",
      status: "completed",
    },
    {
      id: 3,
      student: "Sarah Davis",
      studentId: "STU003",
      items: ["Pizza Slice", "Chocolate Cake"],
      total: 7.0,
      timestamp: "2024-01-15 11:45:00",
      status: "completed",
    },
  ];

  const tabs = [
    { id: "pos", label: "POS System", icon: ShoppingCart },
    { id: "wallet", label: "NFC Wallet", icon: CreditCard },
    { id: "inventory", label: "Inventory", icon: Package },
    { id: "parent", label: "Parent Controls", icon: Users },
    { id: "reports", label: "Reports", icon: TrendingUp },
  ];

  const addToCart = (item) => {
    if (item.stock <= 0) {
      showNotification("Item is out of stock", "error");
      return;
    }

    const existingItem = cart.find((cartItem) => cartItem.id === item.id);
    if (existingItem) {
      if (existingItem.quantity >= item.stock) {
        showNotification("Not enough stock available", "error");
        return;
      }
      setCart(
        cart.map((cartItem) =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        )
      );
    } else {
      setCart([...cart, { ...item, quantity: 1 }]);
    }
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter((item) => item.id !== itemId));
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
    } else {
      const item = inventory.find((i) => i.id === itemId);
      if (item && quantity > item.stock) {
        showNotification("Not enough stock available", "error");
        return;
      }
      setCart(
        cart.map((item) => (item.id === itemId ? { ...item, quantity } : item))
      );
    }
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const processPayment = () => {
    if (!selectedStudent) {
      showNotification("Please select a student first", "error");
      return;
    }

    const total = getCartTotal();
    if (total > selectedStudent.balance) {
      showNotification("Insufficient balance", "error");
      return;
    }

    if (total > selectedStudent.dailyLimit - selectedStudent.spentToday) {
      showNotification("Daily spending limit exceeded", "error");
      return;
    }

    // Check for allergen restrictions
    const restrictedItems = cart.filter((item) =>
      item.allergens.some((allergen) =>
        selectedStudent.restrictions.includes(allergen)
      )
    );

    if (restrictedItems.length > 0) {
      showNotification(
        `Cannot purchase items with allergens: ${restrictedItems
          .map((item) => item.name)
          .join(", ")}`,
        "error"
      );
      return;
    }

    // Update inventory stock
    const updatedInventory = inventory.map((item) => {
      const cartItem = cart.find((ci) => ci.id === item.id);
      if (cartItem) {
        const newStock = item.stock - cartItem.quantity;
        return {
          ...item,
          stock: newStock,
          available: newStock > 0,
        };
      }
      return item;
    });

    setInventory(updatedInventory);

    // Process payment
    const updatedStudents = students.map((student) => {
      if (student.id === selectedStudent.id) {
        return {
          ...student,
          balance: student.balance - total,
          spentToday: student.spentToday + total,
        };
      }
      return student;
    });

    setStudents(updatedStudents);
    setSelectedStudent(
      updatedStudents.find((s) => s.id === selectedStudent.id)
    );

    showNotification(
      `Payment of $${total.toFixed(2)} processed successfully!`,
      "success"
    );
    setCart([]);
  };

  const showNotification = (message, type = "info") => {
    const notification = {
      id: Date.now(),
      message,
      type,
      timestamp: new Date(),
    };
    setNotifications((prev) => [...prev, notification]);

    // Auto remove after 5 seconds
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    }, 5000);
  };

  const handleAddStudent = () => {
    setShowAddStudentModal(true);
  };

  const handleViewStudent = (student) => {
    setSelectedStudentForModal(student);
    setShowStudentModal(true);
  };

  const handleAddFunds = (student) => {
    setSelectedStudentForModal(student);
    setShowAddFundsModal(true);
  };

  const handleStudentSettings = (student) => {
    setSelectedStudentForModal(student);
    setShowStudentModal(true);
  };

  const handleSendNotifications = () => {
    showNotification("Notifications sent to all parents", "success");
  };

  const handleAddFundsToStudent = (student) => {
    setSelectedStudentForModal(student);
    setShowAddFundsModal(true);
  };

  const handleExportStudentData = (student) => {
    const data = {
      student: student.name,
      grade: student.grade,
      balance: student.balance,
      dailyLimit: student.dailyLimit,
      spentToday: student.spentToday,
      restrictions: student.restrictions,
      parent: student.parent,
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${student.name}_data.json`;
    a.click();
    URL.revokeObjectURL(url);

    showNotification(`Data exported for ${student.name}`, "success");
  };

  const handleExportReport = () => {
    const reportData = {
      period: reportPeriod,
      totalRevenue: mockTransactions.reduce((sum, t) => sum + t.total, 0),
      totalTransactions: mockTransactions.length,
      averageOrder:
        mockTransactions.reduce((sum, t) => sum + t.total, 0) /
        mockTransactions.length,
      students: students.map((s) => ({
        name: s.name,
        spentToday: s.spentToday,
        balance: s.balance,
      })),
    };

    const blob = new Blob([JSON.stringify(reportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cafeteria_report_${reportPeriod
      .toLowerCase()
      .replace(" ", "_")}.json`;
    a.click();
    URL.revokeObjectURL(url);

    showNotification("Report exported successfully", "success");
  };

  const addNewStudent = () => {
    if (!newStudent.name || !newStudent.grade || !newStudent.parent) {
      showNotification("Please fill in all required fields", "error");
      return;
    }

    const student = {
      id: `STU${String(students.length + 1).padStart(3, "0")}`,
      name: newStudent.name,
      grade: newStudent.grade,
      balance: newStudent.initialBalance,
      dailyLimit: newStudent.dailyLimit,
      spentToday: 0,
      restrictions: newStudent.restrictions,
      parent: newStudent.parent,
    };

    setStudents([...students, student]);
    setNewStudent({
      name: "",
      grade: "",
      parent: "",
      initialBalance: 0,
      dailyLimit: 20,
      restrictions: [],
    });
    setShowAddStudentModal(false);
    showNotification(`Student ${student.name} added successfully`, "success");
  };

  const addFundsToStudent = () => {
    if (fundsAmount <= 0) {
      showNotification("Please enter a valid amount", "error");
      return;
    }

    const updatedStudents = students.map((student) => {
      if (student.id === selectedStudentForModal.id) {
        return {
          ...student,
          balance: student.balance + fundsAmount,
        };
      }
      return student;
    });

    setStudents(updatedStudents);
    setFundsAmount(0);
    setShowAddFundsModal(false);
    showNotification(
      `$${fundsAmount.toFixed(2)} added to ${
        selectedStudentForModal.name
      }'s account`,
      "success"
    );
  };

  const updateStudentSettings = (studentId, updates) => {
    const updatedStudents = students.map((student) => {
      if (student.id === studentId) {
        return { ...student, ...updates };
      }
      return student;
    });
    setStudents(updatedStudents);
    showNotification("Student settings updated", "success");
  };

  // Inventory Management Functions
  const getLowStockItems = () => {
    return inventory.filter((item) => item.stock <= item.minStock);
  };

  const getInventoryValue = () => {
    return inventory.reduce((total, item) => total + item.stock * item.cost, 0);
  };

  const getTotalItems = () => {
    return inventory.reduce((total, item) => total + item.stock, 0);
  };

  const handleAddItem = () => {
    setShowAddItemModal(true);
  };

  const handleEditItem = (item) => {
    setSelectedItemForEdit(item);
    setNewItem({
      name: item.name,
      category: item.category,
      price: item.price,
      stock: item.stock,
      minStock: item.minStock,
      supplier: item.supplier,
      allergens: item.allergens,
      image: item.image,
    });
    setShowEditItemModal(true);
  };

  const handleDeleteItem = (itemId) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      setInventory(inventory.filter((item) => item.id !== itemId));
      showNotification("Item deleted successfully", "success");
    }
  };

  const addNewItem = () => {
    if (!newItem.name || !newItem.category || newItem.price <= 0) {
      showNotification("Please fill in all required fields", "error");
      return;
    }

    const item = {
      id: Math.max(...inventory.map((i) => i.id)) + 1,
      name: newItem.name,
      category: newItem.category,
      price: newItem.price,
      stock: newItem.stock,
      minStock: newItem.minStock,
      supplier: newItem.supplier,
      allergens: newItem.allergens,
      image: newItem.image || "🍽️",
      available: newItem.stock > 0,
      lastRestocked: new Date().toISOString().split("T")[0],
      cost: newItem.price * 0.6, // Assume 40% markup
    };

    setInventory([...inventory, item]);
    setNewItem({
      name: "",
      category: "",
      price: 0,
      stock: 0,
      minStock: 5,
      supplier: "",
      allergens: [],
      image: "",
    });
    setShowAddItemModal(false);
    showNotification(`Item ${item.name} added successfully`, "success");
  };

  const updateItem = () => {
    if (!newItem.name || !newItem.category || newItem.price <= 0) {
      showNotification("Please fill in all required fields", "error");
      return;
    }

    const updatedInventory = inventory.map((item) => {
      if (item.id === selectedItemForEdit.id) {
        return {
          ...item,
          name: newItem.name,
          category: newItem.category,
          price: newItem.price,
          stock: newItem.stock,
          minStock: newItem.minStock,
          supplier: newItem.supplier,
          allergens: newItem.allergens,
          image: newItem.image || item.image,
          available: newItem.stock > 0,
        };
      }
      return item;
    });

    setInventory(updatedInventory);
    setNewItem({
      name: "",
      category: "",
      price: 0,
      stock: 0,
      minStock: 5,
      supplier: "",
      allergens: [],
      image: "",
    });
    setShowEditItemModal(false);
    setSelectedItemForEdit(null);
    showNotification("Item updated successfully", "success");
  };

  const restockItem = (itemId, quantity) => {
    const updatedInventory = inventory.map((item) => {
      if (item.id === itemId) {
        return {
          ...item,
          stock: item.stock + quantity,
          available: item.stock + quantity > 0,
          lastRestocked: new Date().toISOString().split("T")[0],
        };
      }
      return item;
    });

    setInventory(updatedInventory);
    showNotification(`Item restocked with ${quantity} units`, "success");
  };

  const filteredMenu =
    selectedCategory === "All Categories"
      ? mockMenu
      : mockMenu.filter((item) => item.category === selectedCategory);

  const renderPOSSystem = () => (
    <div className="pos-section">
      <div className="pos-layout">
        <div className="menu-section">
          <div className="section-header">
            <h3>Menu Items</h3>
            <div className="category-filter">
              <select
                className="form-select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option>All Categories</option>
                <option>Main Course</option>
                <option>Salad</option>
                <option>Beverage</option>
                <option>Dessert</option>
              </select>
            </div>
          </div>

          <div className="menu-grid">
            {filteredMenu.map((item) => (
              <div
                key={item.id}
                className={`menu-item ${!item.available ? "unavailable" : ""}`}
              >
                <div className="item-image">{item.image}</div>
                <div className="item-info">
                  <h4>{item.name}</h4>
                  <p className="item-category">{item.category}</p>
                  <p className="item-price">${item.price.toFixed(2)}</p>
                  {item.allergens.length > 0 && (
                    <div className="allergens">
                      <span>⚠️ {item.allergens.join(", ")}</span>
                    </div>
                  )}
                </div>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => addToCart(item)}
                  disabled={!item.available}
                >
                  Add to Cart
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="cart-section">
          <div className="cart-header">
            <h3>Shopping Cart</h3>
            <span className="cart-count">{cart.length} items</span>
          </div>

          <div className="cart-items">
            {cart.length === 0 ? (
              <p className="empty-cart">Cart is empty</p>
            ) : (
              cart.map((item) => (
                <div key={item.id} className="cart-item">
                  <div className="item-details">
                    <span className="item-name">{item.name}</span>
                    <span className="item-price">${item.price.toFixed(2)}</span>
                  </div>
                  <div className="quantity-controls">
                    <button
                      className="quantity-btn"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    >
                      <Minus size={16} />
                    </button>
                    <span className="quantity">{item.quantity}</span>
                    <button
                      className="quantity-btn"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                  <button
                    className="remove-btn"
                    onClick={() => removeFromCart(item.id)}
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="cart-footer">
            <div className="cart-total">
              <span>Total: ${getCartTotal().toFixed(2)}</span>
            </div>
            <button
              className="btn btn-success"
              onClick={processPayment}
              disabled={cart.length === 0 || !selectedStudent}
            >
              Process Payment
            </button>
          </div>
        </div>
      </div>

      <div className="student-selection">
        <h3>Select Student</h3>
        <div className="student-cards">
          {students.map((student) => (
            <div
              key={student.id}
              className={`student-card ${
                selectedStudent?.id === student.id ? "selected" : ""
              }`}
              onClick={() => setSelectedStudent(student)}
            >
              <div className="student-info">
                <h4>{student.name}</h4>
                <p>
                  {student.grade} • {student.id}
                </p>
                <div className="balance-info">
                  <span className="balance">${student.balance.toFixed(2)}</span>
                  <span className="daily-limit">
                    Daily: ${student.dailyLimit.toFixed(2)}
                  </span>
                </div>
                <div className="spent-today">
                  Spent today: ${student.spentToday.toFixed(2)}
                </div>
              </div>
              <div className="student-status">
                {student.restrictions.length > 0 && (
                  <span className="restriction-badge">
                    <AlertTriangle size={12} />
                    Restrictions
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderNFCWallet = () => (
    <div className="wallet-section">
      <div className="section-header">
        <h3>NFC Wallet Management</h3>
        <button className="btn btn-primary" onClick={handleAddStudent}>
          <Plus size={16} />
          Add Student
        </button>
      </div>

      <div className="wallet-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <CreditCard size={24} />
          </div>
          <div className="stat-content">
            <h3>{students.length}</h3>
            <p>Active Wallets</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <h3>
              ${students.reduce((sum, s) => sum + s.balance, 0).toFixed(2)}
            </h3>
            <p>Total Balance</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <ShoppingCart size={24} />
          </div>
          <div className="stat-content">
            <h3>{mockTransactions.length}</h3>
            <p>Today's Transactions</p>
          </div>
        </div>
      </div>

      <div className="wallet-table">
        <table>
          <thead>
            <tr>
              <th>Student</th>
              <th>Balance</th>
              <th>Daily Limit</th>
              <th>Spent Today</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {students.map((student) => (
              <tr key={student.id}>
                <td>
                  <div className="student-cell">
                    <strong>{student.name}</strong>
                    <span>
                      {student.grade} • {student.id}
                    </span>
                  </div>
                </td>
                <td>${student.balance.toFixed(2)}</td>
                <td>${student.dailyLimit.toFixed(2)}</td>
                <td>${student.spentToday.toFixed(2)}</td>
                <td>
                  <span
                    className={`status-badge ${
                      student.balance > 5 ? "active" : "low"
                    }`}
                  >
                    {student.balance > 5 ? "Active" : "Low Balance"}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => handleViewStudent(student)}
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => handleAddFunds(student)}
                    >
                      <Plus size={14} />
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => handleStudentSettings(student)}
                    >
                      <Settings size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderParentControls = () => (
    <div className="parent-section">
      <div className="section-header">
        <h3>Parent Controls & Monitoring</h3>
        <button className="btn btn-primary" onClick={handleSendNotifications}>
          <Bell size={16} />
          Send Notifications
        </button>
      </div>

      <div className="parent-dashboard">
        <div className="parent-cards">
          {students.map((student) => (
            <div key={student.id} className="parent-card">
              <div className="card-header">
                <h4>{student.name}</h4>
                <span className="grade-badge">{student.grade}</span>
              </div>

              <div className="card-content">
                <div className="balance-section">
                  <div className="balance-item">
                    <span className="label">Current Balance</span>
                    <span className="value">${student.balance.toFixed(2)}</span>
                  </div>
                  <div className="balance-item">
                    <span className="label">Daily Limit</span>
                    <span className="value">
                      ${student.dailyLimit.toFixed(2)}
                    </span>
                  </div>
                  <div className="balance-item">
                    <span className="label">Spent Today</span>
                    <span className="value spent">
                      ${student.spentToday.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="controls-section">
                  <h5>Parent Controls</h5>
                  <div className="control-item">
                    <label>Daily Spending Limit</label>
                    <input
                      type="number"
                      defaultValue={student.dailyLimit}
                      className="form-input"
                    />
                  </div>
                  <div className="control-item">
                    <label>Food Restrictions</label>
                    <div className="restrictions">
                      {["nuts", "dairy", "gluten", "eggs"].map((allergen) => (
                        <label key={allergen} className="checkbox-label">
                          <input
                            type="checkbox"
                            defaultChecked={student.restrictions.includes(
                              allergen
                            )}
                          />
                          {allergen}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="control-item">
                    <label>Auto Top-up</label>
                    <select className="form-select">
                      <option>Disabled</option>
                      <option>When balance &lt; $5</option>
                      <option>When balance &lt; $10</option>
                      <option>Weekly</option>
                    </select>
                  </div>
                </div>

                <div className="recent-activity">
                  <h5>Recent Purchases</h5>
                  <div className="activity-list">
                    {mockTransactions
                      .filter((t) => t.studentId === student.id)
                      .slice(0, 3)
                      .map((transaction) => (
                        <div key={transaction.id} className="activity-item">
                          <span>{transaction.items.join(", ")}</span>
                          <span>${transaction.total.toFixed(2)}</span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>

              <div className="card-actions">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => handleAddFundsToStudent(student)}
                >
                  <Plus size={14} />
                  Add Funds
                </button>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => handleExportStudentData(student)}
                >
                  <Download size={14} />
                  Export
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderInventory = () => (
    <div className="inventory-section">
      <div className="section-header">
        <h3>Inventory Management</h3>
        <button className="btn btn-primary" onClick={handleAddItem}>
          <Plus size={16} />
          Add Item
        </button>
      </div>

      {/* Inventory Stats */}
      <div className="inventory-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <Package size={24} />
          </div>
          <div className="stat-content">
            <h3>{getTotalItems()}</h3>
            <p>Total Items</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <AlertCircle size={24} />
          </div>
          <div className="stat-content">
            <h3>{getLowStockItems().length}</h3>
            <p>Low Stock Items</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <h3>${getInventoryValue().toFixed(2)}</h3>
            <p>Inventory Value</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <BarChart3 size={24} />
          </div>
          <div className="stat-content">
            <h3>{inventory.length}</h3>
            <p>Product Types</p>
          </div>
        </div>
      </div>

      {/* Low Stock Alerts */}
      {getLowStockItems().length > 0 && (
        <div className="low-stock-alerts">
          <h4>⚠️ Low Stock Alerts</h4>
          <div className="alert-items">
            {getLowStockItems().map((item) => (
              <div key={item.id} className="alert-item">
                <span className="item-name">{item.name}</span>
                <span className="stock-level">
                  {item.stock} / {item.minStock} min
                </span>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => restockItem(item.id, 20)}
                >
                  Restock
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inventory Table */}
      <div className="inventory-table">
        <table>
          <thead>
            <tr>
              <th>Item</th>
              <th>Category</th>
              <th>Stock</th>
              <th>Min Stock</th>
              <th>Price</th>
              <th>Cost</th>
              <th>Supplier</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {inventory.map((item) => (
              <tr key={item.id}>
                <td>
                  <div className="item-cell">
                    <span className="item-emoji">{item.image}</span>
                    <div className="item-details">
                      <strong>{item.name}</strong>
                      {item.allergens.length > 0 && (
                        <span className="allergens">
                          ⚠️ {item.allergens.join(", ")}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td>{item.category}</td>
                <td>
                  <span
                    className={`stock-level ${
                      item.stock <= item.minStock ? "low" : ""
                    }`}
                  >
                    {item.stock}
                  </span>
                </td>
                <td>{item.minStock}</td>
                <td>${item.price.toFixed(2)}</td>
                <td>${item.cost.toFixed(2)}</td>
                <td>{item.supplier}</td>
                <td>
                  <span
                    className={`status-badge ${
                      item.available ? "active" : "unavailable"
                    }`}
                  >
                    {item.available ? "Available" : "Out of Stock"}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => handleEditItem(item)}
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => restockItem(item.id, 10)}
                    >
                      <Plus size={14} />
                    </button>
                    <button
                      className="btn btn-outline btn-sm btn-danger"
                      onClick={() => handleDeleteItem(item.id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderReports = () => (
    <div className="reports-section">
      <div className="section-header">
        <h3>Sales & Analytics Reports</h3>
        <div className="report-filters">
          <select
            className="form-select"
            value={reportPeriod}
            onChange={(e) => setReportPeriod(e.target.value)}
          >
            <option>Today</option>
            <option>This Week</option>
            <option>This Month</option>
            <option>Custom Range</option>
          </select>
          <button className="btn btn-primary" onClick={handleExportReport}>
            <Download size={16} />
            Export Report
          </button>
        </div>
      </div>

      <div className="reports-grid">
        <div className="report-card">
          <h4>Sales Summary</h4>
          <div className="report-content">
            <div className="report-item">
              <span>Total Revenue</span>
              <span>$1,234.50</span>
            </div>
            <div className="report-item">
              <span>Transactions</span>
              <span>156</span>
            </div>
            <div className="report-item">
              <span>Average Order</span>
              <span>$7.91</span>
            </div>
          </div>
        </div>

        <div className="report-card">
          <h4>Popular Items</h4>
          <div className="report-content">
            <div className="report-item">
              <span>Chicken Burger</span>
              <span>45 orders</span>
            </div>
            <div className="report-item">
              <span>Pizza Slice</span>
              <span>38 orders</span>
            </div>
            <div className="report-item">
              <span>Fresh Juice</span>
              <span>32 orders</span>
            </div>
          </div>
        </div>

        <div className="report-card">
          <h4>Student Spending</h4>
          <div className="report-content">
            {students.map((student) => (
              <div key={student.id} className="report-item">
                <span>{student.name}</span>
                <span>${student.spentToday.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="report-card">
          <h4>Inventory Summary</h4>
          <div className="report-content">
            <div className="report-item">
              <span>Total Items</span>
              <span>{getTotalItems()}</span>
            </div>
            <div className="report-item">
              <span>Low Stock Items</span>
              <span>{getLowStockItems().length}</span>
            </div>
            <div className="report-item">
              <span>Inventory Value</span>
              <span>${getInventoryValue().toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderModals = () => (
    <>
      {/* Add Student Modal */}
      {showAddStudentModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New Student</h3>
              <button
                className="modal-close"
                onClick={() => setShowAddStudentModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Student Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={newStudent.name}
                  onChange={(e) =>
                    setNewStudent({ ...newStudent, name: e.target.value })
                  }
                  placeholder="Enter student name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Grade *</label>
                <input
                  type="text"
                  className="form-input"
                  value={newStudent.grade}
                  onChange={(e) =>
                    setNewStudent({ ...newStudent, grade: e.target.value })
                  }
                  placeholder="e.g., Grade 10"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Parent Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={newStudent.parent}
                  onChange={(e) =>
                    setNewStudent({ ...newStudent, parent: e.target.value })
                  }
                  placeholder="Enter parent name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Initial Balance</label>
                <input
                  type="number"
                  className="form-input"
                  value={newStudent.initialBalance}
                  onChange={(e) =>
                    setNewStudent({
                      ...newStudent,
                      initialBalance: parseFloat(e.target.value) || 0,
                    })
                  }
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Daily Limit</label>
                <input
                  type="number"
                  className="form-input"
                  value={newStudent.dailyLimit}
                  onChange={(e) =>
                    setNewStudent({
                      ...newStudent,
                      dailyLimit: parseFloat(e.target.value) || 20,
                    })
                  }
                  placeholder="20.00"
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Food Restrictions</label>
                <div className="restrictions">
                  {["nuts", "dairy", "gluten", "eggs"].map((allergen) => (
                    <label key={allergen} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={newStudent.restrictions.includes(allergen)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewStudent({
                              ...newStudent,
                              restrictions: [
                                ...newStudent.restrictions,
                                allergen,
                              ],
                            });
                          } else {
                            setNewStudent({
                              ...newStudent,
                              restrictions: newStudent.restrictions.filter(
                                (r) => r !== allergen
                              ),
                            });
                          }
                        }}
                      />
                      {allergen}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setShowAddStudentModal(false)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={addNewStudent}>
                Add Student
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Funds Modal */}
      {showAddFundsModal && selectedStudentForModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add Funds to {selectedStudentForModal.name}</h3>
              <button
                className="modal-close"
                onClick={() => setShowAddFundsModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Current Balance</label>
                <div className="balance-display">
                  ${selectedStudentForModal.balance.toFixed(2)}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Amount to Add</label>
                <input
                  type="number"
                  className="form-input"
                  value={fundsAmount}
                  onChange={(e) =>
                    setFundsAmount(parseFloat(e.target.value) || 0)
                  }
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label className="form-label">New Balance</label>
                <div className="balance-display">
                  ${(selectedStudentForModal.balance + fundsAmount).toFixed(2)}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setShowAddFundsModal(false)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={addFundsToStudent}>
                Add Funds
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Student Details Modal */}
      {showStudentModal && selectedStudentForModal && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h3>Student Details - {selectedStudentForModal.name}</h3>
              <button
                className="modal-close"
                onClick={() => setShowStudentModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="student-details-grid">
                <div className="detail-section">
                  <h4>Basic Information</h4>
                  <div className="detail-item">
                    <span className="label">Name:</span>
                    <span className="value">
                      {selectedStudentForModal.name}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Grade:</span>
                    <span className="value">
                      {selectedStudentForModal.grade}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Student ID:</span>
                    <span className="value">{selectedStudentForModal.id}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Parent:</span>
                    <span className="value">
                      {selectedStudentForModal.parent}
                    </span>
                  </div>
                </div>
                <div className="detail-section">
                  <h4>Financial Information</h4>
                  <div className="detail-item">
                    <span className="label">Current Balance:</span>
                    <span className="value balance">
                      ${selectedStudentForModal.balance.toFixed(2)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Daily Limit:</span>
                    <span className="value">
                      ${selectedStudentForModal.dailyLimit.toFixed(2)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Spent Today:</span>
                    <span className="value spent">
                      ${selectedStudentForModal.spentToday.toFixed(2)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Remaining Today:</span>
                    <span className="value">
                      $
                      {(
                        selectedStudentForModal.dailyLimit -
                        selectedStudentForModal.spentToday
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="detail-section">
                  <h4>Restrictions</h4>
                  {selectedStudentForModal.restrictions.length > 0 ? (
                    <div className="restrictions-list">
                      {selectedStudentForModal.restrictions.map(
                        (restriction) => (
                          <span key={restriction} className="restriction-badge">
                            {restriction}
                          </span>
                        )
                      )}
                    </div>
                  ) : (
                    <p className="no-restrictions">No restrictions</p>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setShowStudentModal(false)}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowStudentModal(false);
                  handleAddFunds(selectedStudentForModal);
                }}
              >
                Add Funds
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Item Modal */}
      {showAddItemModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New Item</h3>
              <button
                className="modal-close"
                onClick={() => setShowAddItemModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Item Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={newItem.name}
                  onChange={(e) =>
                    setNewItem({ ...newItem, name: e.target.value })
                  }
                  placeholder="Enter item name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Category *</label>
                <select
                  className="form-select"
                  value={newItem.category}
                  onChange={(e) =>
                    setNewItem({ ...newItem, category: e.target.value })
                  }
                >
                  <option value="">Select Category</option>
                  <option>Main Course</option>
                  <option>Salad</option>
                  <option>Beverage</option>
                  <option>Dessert</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Price *</label>
                <input
                  type="number"
                  className="form-input"
                  value={newItem.price}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      price: parseFloat(e.target.value) || 0,
                    })
                  }
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Initial Stock</label>
                <input
                  type="number"
                  className="form-input"
                  value={newItem.stock}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      stock: parseInt(e.target.value) || 0,
                    })
                  }
                  placeholder="0"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Minimum Stock</label>
                <input
                  type="number"
                  className="form-input"
                  value={newItem.minStock}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      minStock: parseInt(e.target.value) || 5,
                    })
                  }
                  placeholder="5"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Supplier</label>
                <input
                  type="text"
                  className="form-input"
                  value={newItem.supplier}
                  onChange={(e) =>
                    setNewItem({ ...newItem, supplier: e.target.value })
                  }
                  placeholder="Enter supplier name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Emoji/Icon</label>
                <input
                  type="text"
                  className="form-input"
                  value={newItem.image}
                  onChange={(e) =>
                    setNewItem({ ...newItem, image: e.target.value })
                  }
                  placeholder="🍽️"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Allergens</label>
                <div className="restrictions">
                  {["nuts", "dairy", "gluten", "eggs"].map((allergen) => (
                    <label key={allergen} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={newItem.allergens.includes(allergen)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewItem({
                              ...newItem,
                              allergens: [...newItem.allergens, allergen],
                            });
                          } else {
                            setNewItem({
                              ...newItem,
                              allergens: newItem.allergens.filter(
                                (r) => r !== allergen
                              ),
                            });
                          }
                        }}
                      />
                      {allergen}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setShowAddItemModal(false)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={addNewItem}>
                Add Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Item Modal */}
      {showEditItemModal && selectedItemForEdit && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Item - {selectedItemForEdit.name}</h3>
              <button
                className="modal-close"
                onClick={() => setShowEditItemModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Item Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={newItem.name}
                  onChange={(e) =>
                    setNewItem({ ...newItem, name: e.target.value })
                  }
                  placeholder="Enter item name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Category *</label>
                <select
                  className="form-select"
                  value={newItem.category}
                  onChange={(e) =>
                    setNewItem({ ...newItem, category: e.target.value })
                  }
                >
                  <option value="">Select Category</option>
                  <option>Main Course</option>
                  <option>Salad</option>
                  <option>Beverage</option>
                  <option>Dessert</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Price *</label>
                <input
                  type="number"
                  className="form-input"
                  value={newItem.price}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      price: parseFloat(e.target.value) || 0,
                    })
                  }
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Current Stock</label>
                <input
                  type="number"
                  className="form-input"
                  value={newItem.stock}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      stock: parseInt(e.target.value) || 0,
                    })
                  }
                  placeholder="0"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Minimum Stock</label>
                <input
                  type="number"
                  className="form-input"
                  value={newItem.minStock}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      minStock: parseInt(e.target.value) || 5,
                    })
                  }
                  placeholder="5"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Supplier</label>
                <input
                  type="text"
                  className="form-input"
                  value={newItem.supplier}
                  onChange={(e) =>
                    setNewItem({ ...newItem, supplier: e.target.value })
                  }
                  placeholder="Enter supplier name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Emoji/Icon</label>
                <input
                  type="text"
                  className="form-input"
                  value={newItem.image}
                  onChange={(e) =>
                    setNewItem({ ...newItem, image: e.target.value })
                  }
                  placeholder="🍽️"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Allergens</label>
                <div className="restrictions">
                  {["nuts", "dairy", "gluten", "eggs"].map((allergen) => (
                    <label key={allergen} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={newItem.allergens.includes(allergen)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewItem({
                              ...newItem,
                              allergens: [...newItem.allergens, allergen],
                            });
                          } else {
                            setNewItem({
                              ...newItem,
                              allergens: newItem.allergens.filter(
                                (r) => r !== allergen
                              ),
                            });
                          }
                        }}
                      />
                      {allergen}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setShowEditItemModal(false)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={updateItem}>
                Update Item
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const renderNotifications = () => (
    <div className="notifications-container">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className={`notification notification-${notification.type}`}
        >
          <div className="notification-content">
            <span className="notification-message">{notification.message}</span>
            <button
              className="notification-close"
              onClick={() =>
                setNotifications((prev) =>
                  prev.filter((n) => n.id !== notification.id)
                )
              }
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="cafeteria-system">
      <div className="page-header">
        <h1>Cafeteria & Cashless Payment System</h1>
        <p>NFC wallet management, POS system, and parent controls</p>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`tab ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={20} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="tab-content">
        {activeTab === "pos" && renderPOSSystem()}
        {activeTab === "wallet" && renderNFCWallet()}
        {activeTab === "inventory" && renderInventory()}
        {activeTab === "parent" && renderParentControls()}
        {activeTab === "reports" && renderReports()}
      </div>

      {renderModals()}
      {renderNotifications()}
    </div>
  );
};

export default CafeteriaSystem;
